import { Component, type ErrorInfo, type ReactNode } from "react";
import posthog from "@/lib/posthog";

interface Props {
  children: ReactNode;
  fallback?: (resetError: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, componentStack: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
    posthog.captureException(error, {
      component_stack: info.componentStack,
    });
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null, componentStack: null });
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.reset);
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-8 text-center">
        <h1 className="text-2xl font-bold text-destructive">Algo salió mal</h1>
        <p className="text-muted-foreground max-w-md">
          Ocurrió un error inesperado. Nuestro equipo ha sido notificado automáticamente.
        </p>
        {this.state.error && (
          <details className="text-left max-w-2xl w-full">
            <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
              Detalles del error
            </summary>
            <pre className="mt-2 p-3 rounded-md bg-muted text-xs overflow-auto max-h-60 whitespace-pre-wrap break-words">
              {this.state.error.message}
              {"\n\n"}
              {this.state.error.stack}
              {this.state.componentStack && (
                <>
                  {"\n\n--- Component Stack ---\n"}
                  {this.state.componentStack}
                </>
              )}
            </pre>
          </details>
        )}
        <button
          onClick={() => { this.reset(); window.location.reload(); }}
          className="px-6 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition"
        >
          Recargar página
        </button>
      </div>
    );
  }
}
